import type { ChipProps } from "@mui/material";
import type {
  Game,
  GameStatus,
  PlayerDirectory,
  PlayerIdentity,
  SpiddParticipant,
} from "./types";

export const STATUS_LABEL: Record<string, string> = {
  A_JOUER: "À jouer",
  EN_COURS: "En cours",
  TERMINE: "Terminé",
  EXEMPT: "Exempt",
  FORFAIT: "Forfait",
  ABANDON: "Abandon",
};

export function statusLabel(status: GameStatus): string {
  return STATUS_LABEL[status] || String(status);
}

export function statusChipColor(
  status: GameStatus
): ChipProps["color"] {
  switch (status) {
    case "TERMINE":
      return "success";
    case "EN_COURS":
      return "warning";
    case "EXEMPT":
      return "info";
    case "FORFAIT":
    case "ABANDON":
      return "error";
    case "A_JOUER":
    default:
      return "default";
  }
}

function formatDisplayName(
  prenom: string | null | undefined,
  nom: string | null | undefined,
  licence: string
): { displayName: string; hasName: boolean } {
  const first = (prenom ?? "").trim();
  const last = (nom ?? "").trim();
  if (first || last) {
    return {
      displayName: [first, last].filter(Boolean).join(" "),
      hasName: true,
    };
  }
  return { displayName: `Licence ${licence}`, hasName: false };
}

/** Identité depuis le référentiel SPIDD projeté (`participants`). */
export function identityFromSpiddParticipant(
  participant: SpiddParticipant
): PlayerIdentity {
  const { displayName, hasName } = formatDisplayName(
    participant.prenom,
    participant.nom,
    participant.licence
  );
  const club =
    (participant.clubCourt ?? "").trim() ||
    (participant.clubLong ?? "").trim() ||
    null;
  return {
    displayName,
    club,
    points: participant.points ?? null,
    dossard: participant.dossard ?? null,
    hasName,
    nom: participant.nom,
    prenom: participant.prenom,
    clubNumero: participant.clubNumero,
    clubLong: participant.clubLong,
    licence: participant.licence,
  };
}

/** Normalise pour recherche (casse + accents). */
export function normalizeSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function qualificationLabel(
  status: string | undefined
): string | null {
  switch (status) {
    case "QUALIFIED":
      return "Qualifié";
    case "ELIMINATED":
      return "Éliminé";
    case "PENDING":
      return "En attente";
    default:
      return null;
  }
}

/**
 * Identité depuis un document Firestore `players` (secours).
 * Convention dépôt : `name` = nom, `surname` = prénom.
 */
export function identityFromPlayerDoc(
  licenceId: string,
  value: Record<string, unknown>
): PlayerIdentity {
  const lastName =
    typeof value.name === "string" ? value.name.trim() : "";
  const firstName =
    typeof value.surname === "string" ? value.surname.trim() : "";
  const { displayName, hasName } = formatDisplayName(
    firstName,
    lastName,
    licenceId
  );
  const clubRaw =
    typeof value.club === "string" ? value.club.trim() : "";
  const pointsRaw =
    value.points == null || value.points === ""
      ? null
      : Number(value.points);
  return {
    displayName,
    club: clubRaw || null,
    points: Number.isFinite(pointsRaw) ? pointsRaw : null,
    dossard: null,
    hasName,
  };
}

/** Construit le répertoire d’affichage depuis `spiddResultats.participants`. */
export function directoryFromSpiddParticipants(
  participants: SpiddParticipant[] | undefined
): PlayerDirectory {
  const directory: PlayerDirectory = {};
  if (!participants) return directory;
  for (const participant of participants) {
    if (!participant?.licence) continue;
    directory[String(participant.licence)] =
      identityFromSpiddParticipant(participant);
  }
  return directory;
}

export function resolvePlayer(
  licence: string | null | undefined,
  directory: PlayerDirectory,
  emptyLabel = "À déterminer"
): PlayerIdentity {
  if (!licence) {
    return {
      displayName: emptyLabel,
      club: null,
      points: null,
      dossard: null,
      hasName: false,
    };
  }
  return (
    directory[licence] ?? {
      displayName: `Licence ${licence}`,
      club: null,
      points: null,
      dossard: null,
      hasName: false,
    }
  );
}

export function displayPlayerName(
  licence: string | null | undefined,
  directory: PlayerDirectory,
  emptyLabel = "À déterminer"
): string {
  return resolvePlayer(licence, directory, emptyLabel).displayName;
}

export function formatPoints(points: number | null | undefined): string | null {
  if (points == null || !Number.isFinite(points)) return null;
  return `${points.toLocaleString("fr-FR")} pts`;
}

export function formatSecondaryLine(player: PlayerIdentity): string | null {
  const parts: string[] = [];
  if (player.club) parts.push(player.club);
  const pts = formatPoints(player.points);
  if (pts) parts.push(pts);
  return parts.length ? parts.join(" · ") : null;
}

export function formatSetScore(game: Game): string {
  if (game.status === "EXEMPT") return "Exempt";
  if (game.status === "FORFAIT" && game.sets.length === 0) return "Forfait";
  if (game.status === "ABANDON" && game.sets.length === 0) return "Abandon";
  if (game.sets.length > 0 || game.status === "TERMINE") {
    const a = game.setWins[0] ?? 0;
    const b = game.setWins[1] ?? 0;
    return `${a}–${b}`;
  }
  return "—";
}

export function formatManches(game: Game): string | null {
  if (!game.sets.length) return null;
  return game.sets.map((set) => `${set.score1}–${set.score2}`).join(" · ");
}

export function isWinner(
  licence: string | null | undefined,
  game: Game
): boolean {
  return Boolean(licence && game.winner && licence === game.winner);
}

export function formatCapturedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/**
 * Extrait le code tableau Firestore (`name`) depuis un libellé SPIDD.
 * Ex. « TABLEAU A » → « A ».
 */
export function extractSpiddBoardName(spiddLabel: string): string | null {
  const trimmed = spiddLabel.trim();
  if (!trimmed) return null;
  const prefixed = trimmed.match(/^TABLEAU\s+([A-Za-z0-9]+)\b/i);
  if (prefixed?.[1]) return prefixed[1].toUpperCase();
  if (/^[A-Za-z0-9]{1,3}$/.test(trimmed)) return trimmed.toUpperCase();
  return null;
}

export function findBoardForSpiddLabel<
  T extends { name: string; label: string },
>(boards: T[], spiddLabel: string): T | null {
  const code = extractSpiddBoardName(spiddLabel);
  if (!code) return null;
  return (
    boards.find((b) => b.name.trim().toUpperCase() === code) ?? null
  );
}

/**
 * Titre public : `label` Firestore du tableau dont `name` correspond au
 * libellé SPIDD (« TABLEAU A » → name « A »). Sinon fallback SPIDD.
 */
export function tableauDisplayTitle(
  doc: {
    label: string;
    eventLabel?: string | null;
  },
  boards: { name: string; label: string }[] = []
): string {
  const board = findBoardForSpiddLabel(boards, doc.label);
  const boardLabel = board?.label?.trim();
  if (boardLabel) return boardLabel;

  const label = doc.label?.trim() || "Tableau";
  const event = doc.eventLabel?.trim() || "";
  if (event && event !== label) return `${label} · ${event}`;
  return label;
}
